import { Actions, type SetActiveEventIdActionData, type SetPopupStateActionData, type SetViewportActionData } from "@client/actions";
import { combineMultipleReducers, combineReducers, isBrowser } from "@client/lib/misc";
import { getScreenType } from "@client/lib/viewport";
import {
  type BaseAppState,
  type ContentAppState,
  type Context,
  type FunnelInfoAppState,
  type GenericMap,
  type Nullable,
  ScreenType
} from "@client/types";
import { type Api } from "@client/types/Api";
import { type EventsAppState } from "@client/types/AppState/event";
import { type ViewportAppState } from "@client/types/AppState/viewport";
import { type ReduxAction } from "@client/types/ReduxAction";
import { updateItemBy } from "@common/lib/array";
import { isUndefined } from "lodash";
import { type InitSuccessActionData, type LoadPageSuccessActionData } from "./app/actions";
import { type ComponentAppState } from "./types/AppState/component";
import { type LabelsAppState } from "./types/AppState/labels";
import { type LayoutAppState } from "./types/AppState/layouts";
import { type PageAppState, type WithPageAppState } from "./types/AppState/page";
import { type PagePopupAppState, type PopupAppState } from "./types/AppState/popups";
import { type PortalAppState } from "./types/AppState/portal";
import { type StyleSheetAppState } from "./types/AppState/styleSheet";

export const components = (state: ComponentAppState[] = [], action: ReduxAction<any>): ComponentAppState[] => {
  switch (action.type) {
    case Actions.InitSuccess:
    case Actions.LoadPageSuccess: {
      const { components } = action.data as InitSuccessActionData;
      return components ?? state;
    }
    default:
      return state;
  }
};

const defaultEvents: EventsAppState = {
  activeEventId: null,
  items: []
};
const events = (state: EventsAppState = defaultEvents, action: ReduxAction<any>): EventsAppState => {
  switch (action.type) {
    case Actions.InitSuccess: {
      const { events } = action.data as InitSuccessActionData;

      return {
        activeEventId: null,
        items: events
      };
    }
    case Actions.SetActiveEventId: {
      const { id } = action.data as SetActiveEventIdActionData;
      return {
        ...state,
        activeEventId: id
      };
    }
    default:
      return state;
  }
};

export const defaultContent: ContentAppState = {
  entities: {},
  local: {}
};
const content = (state: ContentAppState = defaultContent, action: ReduxAction<any>): ContentAppState => {
  switch (action.type) {
    case Actions.InitSuccess:
    case Actions.LoadPageSuccess: {
      const { content } = action.data as LoadPageSuccessActionData;
      return {
        ...state,
        ...content
      };
    }
    default:
      return state;
  }
};

const error = (state: string | null = null, { type, data }): string | null => {
  switch (type) {
    case Actions.InitFailure:
      return data.error;
    case Actions.InitSuccess:
      return null;
    default:
      return state;
  }
};

export const funnelInfo = (
  state: FunnelInfoAppState | null = null,
  action: ReduxAction<any>
): FunnelInfoAppState | null => {
  switch (action.type) {
    case Actions.InitSuccess:
    case Actions.LoadPageSuccess: {
      const { funnelInfo } = action.data as Nullable<Api.WithFunnelInfo>;
      return funnelInfo;
    }
    default:
      return state;
  }
};

export const layouts = (state: LayoutAppState[] = [], action: ReduxAction<any>): LayoutAppState[] => {
  switch (action.type) {
    case Actions.InitSuccess:
    case Actions.LoadPageSuccess: {
      const { layoutContent, page } = action.data as LoadPageSuccessActionData;
      if (!page.layoutId || !layoutContent) return [];
      if (state.some((x) => x.id === page.layoutId)) {
        return state;
      }

      return [
        {
          id: page.layoutId,
          name: "",
          settings: layoutContent.settings,
          local: layoutContent.local
        }
      ];
    }
    default:
      return state;
  }
};

export const popups = (state: PopupAppState[] = [], action: ReduxAction<any>): PopupAppState[] => {
  switch (action.type) {
    case Actions.InitSuccess:
    case Actions.LoadPageSuccess: {
      const { popups } = action.data as InitSuccessActionData;
      return popups ?? state;
    }
    case Actions.SetPopupState: {
      const data = action.data as SetPopupStateActionData;
      return updateItemBy(
        state,
        (x) => x.id === data.id,
        (x) => ({
          ...x,
          state: data.state
        })
      );
    }
    default:
      return state;
  }
};

const defaultStyleSheet: StyleSheetAppState = {
  data: {
    settings: {},
    structure: [],
    version: ""
  },
  id: "",
  local: null
};
export const styleSheet = (
  state: StyleSheetAppState = defaultStyleSheet,
  action: ReduxAction<any>
): StyleSheetAppState => {
  switch (action.type) {
    case Actions.InitSuccess:
    case Actions.LoadPageSuccess: {
      const { styleSheet } = action.data as InitSuccessActionData;
      return styleSheet ?? state;
    }
    default:
      return state;
  }
};

export const defaultViewport: ViewportAppState = {
  fixedScreen: null,
  screen: isBrowser() ? getScreenType(window.innerWidth) : ScreenType.Desktop
};
export const viewport = (state: ViewportAppState = defaultViewport, action: ReduxAction<any>): ViewportAppState => {
  switch (action.type) {
    case Actions.SetViewport: {
      const { fixedScreen, viewport } = action.data as SetViewportActionData;
      return {
        ...viewport,
        fixedScreen: isUndefined(fixedScreen) ? state.fixedScreen : fixedScreen
      };
    }
    default:
      return state;
  }
};

export const defaultPortal: PortalAppState = {
  eventId: null,
  id: "",
  programId: null,
  faviconMediaId: null,
  faviconUrl: null,
  logoMediaId: null,
  logoUrl: null
};

export const defaultPage: PageAppState = {
  brand: null,
  id: "",
  layoutId: null,
  meta: {
    bodyCode: null,
    description: "",
    footerCode: null,
    headCode: null,
    imageUrl: null,
    keywords: null,
    robots: null,
    title: "",
    type: ""
  },
  postId: null,
  productId: null,
  subType: "Login",
  type: "Web",
  variantId: ""
};
export const page = (state: PageAppState = defaultPage, action: ReduxAction<any>): PageAppState => {
  switch (action.type) {
    case Actions.InitSuccess:
    case Actions.LoadPageSuccess: {
      const { page } = action.data as WithPageAppState;
      return page;
    }
    default:
      return state;
  }
};

const pagePopups = (state: PagePopupAppState[] = [], action: ReduxAction<any>): PagePopupAppState[] => {
  switch (action.type) {
    case Actions.InitSuccess:
    case Actions.LoadPageSuccess: {
      const data = action.data as LoadPageSuccessActionData;
      return data.pagePopups;
    }
    default:
      return state;
  }
};

export const reducers = {
  components,
  content,
  context: (state: Context = "page") => state,
  cultureCode: (state: string = "en-US") => state,
  events,
  funnelInfo,
  isEditMode: (state = false) => state,
  labels: (state: LabelsAppState = {}) => state,
  layouts,
  modules: (state = {}) => state,
  page,
  pagePopups,
  popups,
  portal: (state: PortalAppState = defaultPortal) => state,
  styleSheet,
  viewport
};

export const createReducer = (dynamicReducers: GenericMap<any> = {}) =>
  combineReducers<BaseAppState>({
    ...reducers,
    ...combineMultipleReducers(dynamicReducers)
  });
